import { useState } from 'react'
import { X, Printer, Copy, Download } from 'lucide-react'
import { toast } from 'sonner'
import { Product } from '../types/product'
import { printBarcode } from '../hooks/useBarcodes'

interface BarcodePrintModalProps {
    product: Product
    isOpen: boolean
    onClose: () => void
}

export default function BarcodePrintModal({ product, isOpen, onClose }: BarcodePrintModalProps) {
    const [quantity, setQuantity] = useState(1)
    const [size, setSize] = useState<'40x20' | '50x25' | '60x30'>('40x20')
    const [isLoading, setIsLoading] = useState(false)
    const [barcodeData, setBarcodeData] = useState<{
        pngDataUrl: string
        zpl: string
    } | null>(null)

    const handlePrintBarcode = async () => {
        try {
            setIsLoading(true)

            const response = await printBarcode(product.id, {
                quantity,
                size
            })

            setBarcodeData({
                pngDataUrl: response.pngDataUrl,
                zpl: response.zpl
            })

            toast.success('Barkod başarıyla oluşturuldu')

            // print-js kullanarak doğrudan yazdırma dialogunu aç
            setTimeout(() => {
                handlePrintPNGWithData(response.pngDataUrl)
            }, 500)

        } catch (error) {
            const message = error instanceof Error ? error.message : 'Barkod oluşturulamadı'
            toast.error(message)
        } finally {
            setIsLoading(false)
        }
    }

    const handlePrintPNG = () => {
        if (!barcodeData) return
        handlePrintPNGWithData(barcodeData.pngDataUrl)
    }

    const handlePrintPNGWithData = (pngDataUrl: string) => {
        try {
            // print-js kullanarak doğrudan yazdırma dialogunu aç
            import('print-js').then((printJS) => {
                printJS.default({
                    printable: pngDataUrl,
                    type: 'image',
                    documentTitle: `Barkod - ${product.sku}`
                })
            }).catch((error) => {
                console.error('Print-js import error:', error);
                toast.error('Yazdırma kütüphanesi yüklenemedi');
            });
        } catch (error) {
            console.error('Print error:', error);
            toast.error('Yazdırma dialogu açılırken hata oluştu');
        }
    }

    const handleCopyZPL = async () => {
        if (!barcodeData) return

        try {
            await navigator.clipboard.writeText(barcodeData.zpl)
            toast.success('ZPL kodu kopyalandı')
        } catch (error) {
            toast.error('ZPL kodu kopyalanamadı')
        }
    }

    const handleDownloadPNG = () => {
        if (!barcodeData) return

        const link = document.createElement('a')
        link.href = barcodeData.pngDataUrl
        link.download = `barcode-${product.sku}-${size}.png`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        toast.success('PNG dosyası indirildi')
    }

    if (!isOpen) return null

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                {/* Background overlay */}
                <div
                    className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                    onClick={onClose}
                ></div>

                {/* Modal */}
                <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                    <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-medium text-gray-900">
                                Barkod Yazdır
                            </h3>
                            <button
                                onClick={onClose}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <X className="h-6 w-6" />
                            </button>
                        </div>

                        {/* Product info */}
                        <div className="mb-4 p-3 bg-gray-50 rounded-md">
                            <h4 className="font-medium text-gray-900">{product.name}</h4>
                            <p className="text-sm text-gray-600">SKU: {product.sku}</p>
                            <p className="text-sm text-gray-600">Fiyat: ₺{product.price}</p>
                        </div>

                        {/* Settings */}
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Adet
                                </label>
                                <input
                                    type="number"
                                    min="1"
                                    max="100"
                                    value={quantity}
                                    onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Boyut
                                </label>
                                <select
                                    value={size}
                                    onChange={(e) => setSize(e.target.value as any)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                                >
                                    <option value="40x20">40×20 mm</option>
                                    <option value="50x25">50×25 mm</option>
                                    <option value="60x30">60×30 mm</option>
                                </select>
                            </div>
                        </div>

                        {/* Print button */}
                        <button
                            onClick={handlePrintBarcode}
                            disabled={isLoading}
                            className="mt-4 w-full bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                        >
                            {isLoading ? (
                                <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                    <span>Yazdırılıyor...</span>
                                </>
                            ) : (
                                <>
                                    <Printer className="h-4 w-4" />
                                    <span>Yazdır</span>
                                </>
                            )}
                        </button>

                        {/* Barcode display */}
                        {barcodeData && (
                            <div className="mt-6 p-4 border border-gray-200 rounded-md">
                                <h4 className="font-medium text-gray-900 mb-3">Oluşturulan Barkod</h4>

                                {/* PNG preview - Backend'den gelen PNG'de zaten barkod, barkod numarası ve SKU var */}
                                <div className="mb-4 text-center bg-white p-4 border border-gray-200 rounded">
                                    <img
                                        src={barcodeData.pngDataUrl}
                                        alt="Barkod"
                                        className="mx-auto max-w-full h-auto"
                                        style={{ maxHeight: '300px' }}
                                    />
                                </div>

                                {/* Action buttons */}
                                <div className="grid grid-cols-3 gap-2">
                                    <button
                                        onClick={handlePrintPNG}
                                        className="flex items-center justify-center space-x-1 px-3 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700"
                                    >
                                        <Printer className="h-4 w-4" />
                                        <span>Yazdır</span>
                                    </button>

                                    <button
                                        onClick={handleCopyZPL}
                                        className="flex items-center justify-center space-x-1 px-3 py-2 bg-green-600 text-white text-sm rounded-md hover:bg-green-700"
                                    >
                                        <Copy className="h-4 w-4" />
                                        <span>ZPL Kopyala</span>
                                    </button>

                                    <button
                                        onClick={handleDownloadPNG}
                                        className="flex items-center justify-center space-x-1 px-3 py-2 bg-purple-600 text-white text-sm rounded-md hover:bg-purple-700"
                                    >
                                        <Download className="h-4 w-4" />
                                        <span>İndir</span>
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}
